import { CircleAlertIcon, ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useDialogNodesCache from '@/hooks/useDialogNodesCache';
import useExpressions from '@/hooks/useExpressions';
import useIntents from '@/hooks/useIntents';
import { pushModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';

import { Wrapper } from './Wrapper';
import { MODAL_CONNECT_INTENT } from '../../Modals/ModalConductor';

import styles from './Wrapper.module.scss';

interface Props {
  phraseCount: number;
  intentName: string;
  brainId: string;
  isLoading: boolean;
}

const MINIMUM_EXPRESSIONS_LENGTH = 0;

export const Performance = ({ phraseCount, intentName, brainId, isLoading }: Props) => {
  const { t } = useTranslation();
  const { intent, optimalPhrases } = useIntents(brainId, intentName);
  const { isDraft } = useExpressions(brainId, intentName);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { findUsedNodes } = useDialogNodesCache();
  const slug = useSelector(selectAccountSlug);
  const usedIn = findUsedNodes({ brainId, name: intentName });
  const nLength = usedIn?.length;

  const handleCreateDialog = () => {
    dispatch(pushModal(MODAL_CONNECT_INTENT, { intentName, brainId }));
  };

  return (
    <Wrapper phrases={phraseCount} optimalPhrases={optimalPhrases}>
      {isLoading && !isDraft && <Skeleton className="h-[45px] w-[140px]" />}
      {nLength > 0 && !isLoading && (
        <div className={styles.nodes}>
          <span className="text-sm leading-5 font-semibold text-foreground-muted">
            {t('intent.connected_flows')}
          </span>
          {usedIn.map((x) => (
            <button
              key={x.nodeId}
              type="button"
              onClick={() =>
                navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${x.dialogId}`)
              }
            >
              {x.label}
              <ExternalLinkIcon size={16} className="text-foreground-muted" />
            </button>
          ))}
        </div>
      )}
      {!nLength &&
        !isLoading &&
        intent?.expressions?.length > MINIMUM_EXPRESSIONS_LENGTH &&
        intentName !== 'offtopics' && (
          <Alert variant="warning">
            <CircleAlertIcon size={16} />
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>{t('intent.no_flow')}</span>
              <Button variant="ghost" size="sm" onClick={handleCreateDialog}>
                {t('common.connect')}
              </Button>
            </AlertDescription>
          </Alert>
        )}
    </Wrapper>
  );
};
